import type {
	CreateExpenseRequest,
	ExpenseCategory,
	ExpenseEvidenceType,
	ExpensePaymentMethod
} from '$lib/api/types';

export type ExpenseFormValues = {
	category: ExpenseCategory;
	amount: number | string | null | undefined;
	expenseDate: string;
	description: string;
	attachmentUrl: string;
	approvalLineId: number | string | null | undefined;
	evidenceType?: ExpenseEvidenceType;
	paymentMethod?: ExpensePaymentMethod;
	merchantName?: string;
};

type ValidationResult =
	| { ok: true; request: CreateExpenseRequest }
	| { ok: false; message: string };

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_TEXT_LENGTH = 500;
const MAX_KRW_AMOUNT = 99_999_999;

export function validateExpenseForm(
	values: ExpenseFormValues,
	today = toLocalDateString(new Date())
): ValidationResult {
	const amount = parseNumber(values.amount);
	if (amount == null || !Number.isInteger(amount) || amount < 1 || amount > MAX_KRW_AMOUNT) {
		return { ok: false, message: '금액은 1원 이상 99,999,999원 이하의 정수로 입력해주세요.' };
	}

	if (!values.expenseDate) {
		return { ok: false, message: '지출일을 입력해주세요.' };
	}
	if (!isValidDateString(values.expenseDate)) {
		return { ok: false, message: '지출일 형식이 올바르지 않습니다.' };
	}
	if (values.expenseDate > today) {
		return { ok: false, message: '미래 날짜로 경비를 신청할 수 없습니다.' };
	}

	const approvalLineId = parsePositiveInteger(values.approvalLineId);
	if (!approvalLineId) {
		return { ok: false, message: '결재선을 선택해주세요.' };
	}

	const description = values.description.trim();
	if (description.length > MAX_TEXT_LENGTH) {
		return { ok: false, message: '설명은 500자 이하로 입력해주세요.' };
	}

	const attachmentUrl = values.attachmentUrl.trim();
	if (attachmentUrl.length > MAX_TEXT_LENGTH) {
		return { ok: false, message: '첨부 URL은 500자 이하로 입력해주세요.' };
	}
	if (attachmentUrl && !isHttpUrl(attachmentUrl)) {
		return { ok: false, message: '첨부 URL은 http 또는 https 주소로 입력해주세요.' };
	}

	const merchantName = (values.merchantName ?? '').trim();
	if (merchantName.length > 100) {
		return { ok: false, message: '가맹점은 100자 이하로 입력해주세요.' };
	}

	return {
		ok: true,
		request: compactRequest({
			category: values.category,
			amount,
			expenseDate: values.expenseDate,
			description,
			attachmentUrl,
			evidenceType: values.evidenceType,
			paymentMethod: values.paymentMethod,
			merchantName,
			approvalLineId
		})
	};
}

function parseNumber(value: number | string | null | undefined): number | null {
	const raw = typeof value === 'number' ? String(value) : (value ?? '').trim();
	if (!raw) return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
}

function parsePositiveInteger(value: number | string | null | undefined): number | undefined {
	const raw = typeof value === 'number' ? String(value) : (value ?? '').trim();
	if (!raw) return undefined;
	const parsed = Number(raw);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function isHttpUrl(value: string): boolean {
	try {
		const url = new URL(value);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

function isValidDateString(value: string): boolean {
	if (!DATE_PATTERN.test(value)) return false;
	const [year, month, day] = value.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	return (
		date.getFullYear() === year &&
		date.getMonth() === month - 1 &&
		date.getDate() === day
	);
}

export function toLocalDateString(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

function compactRequest(request: {
	category: ExpenseCategory;
	amount: number;
	expenseDate: string;
	description: string;
	attachmentUrl: string;
	evidenceType?: ExpenseEvidenceType;
	paymentMethod?: ExpensePaymentMethod;
	merchantName: string;
	approvalLineId: number | undefined;
}): CreateExpenseRequest {
	const payload: CreateExpenseRequest = {
		category: request.category,
		amount: request.amount,
		expenseDate: request.expenseDate
	};
	if (request.description) payload.description = request.description;
	if (request.attachmentUrl) payload.attachmentUrl = request.attachmentUrl;
	if (request.evidenceType) payload.evidenceType = request.evidenceType;
	if (request.paymentMethod) payload.paymentMethod = request.paymentMethod;
	if (request.merchantName) payload.merchantName = request.merchantName;
	if (request.approvalLineId) payload.approvalLineId = request.approvalLineId;
	return payload;
}
